/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  CompanyContainer,
  HeaderTitle,
  StyledContainer,
  TableCell,
  TableWrapper,
  IconButton,
  LoaderWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import {
  DeleteConfirmationModal,
  Table,
  IconButton as IconButtonWithText,
  Loader,
  DropdownButton,
} from '../../../Components/uielements';
import SubscriptionPlanService from '../../../services/bioplus-services/subscription-plan';
import { AddPlusIcon, DeleteIcon, EditIcon } from '../../../Components/icons';
import { AddOnPlan } from './modals';
import {
  CreateSubscriptionPlanDefaultValues,
  addOnDefaultValues,
  getBillingPeriodLabel,
} from './constant';
import { IntlMessages } from '../../../helper/langHelper';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import moment from 'moment';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import SubscriptionModalV2 from './modals/SubscriptionModalV2';
import { getFeatureValue } from './helper';
// import SubscriptionEditModal from './modals/subscriptionEditModal';

export function extractData(dataArray) {
  if (dataArray) {
    const extractedData = dataArray?.items?.map((item) => {
      return {
        planName: item.name,
        planPrice: item.baseCost,
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const SubscriptionPlan = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [deletePlan, setDeletePlan] = useState();
  const [editSubscriptionPlan, setEditSubscriptionPlan] = useState();
  const [createPlan, setCreatePlan] = useState();
  const [addOnPlan, setAddOnPlan] = useState();

  const query = useQuery(['subscriptionPlans', page, pageSize], () =>
    SubscriptionPlanService.getPlans(page, pageSize)
  );

  const tableData = query?.data?.filter((plan) => plan?.type !== 'addOn') ?? [];

  // const tableDataAddOn = query?.data?.filter((plan) => plan?.type === 'addOn') ?? [];

  const queryClient = useQueryClient();
  const deleteMutation = useMutation((id) => SubscriptionPlanService.remove(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('subscriptionPlans');
      setDeletePlan(undefined);
    },
  });

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  //   table coulmns
  const userColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.plan.name" />,
        accessor: 'labelName',
        width: '19%',
      },
      {
        Header: <IntlMessages id="table.col.period" />,
        accessor: 'billingPeriod',
        Cell: (cellData) => <TableCell>{getBillingPeriodLabel(cellData.value)}</TableCell>,
        width: '13%',
      },
      {
        Header: <IntlMessages id="table.col.price" />,
        accessor: 'baseCost',
        Cell: (cellData) => <TableCell>{cellData.value ?? '--'}</TableCell>,
        width: '10%',
      },

      {
        Header: <IntlMessages id="nav.premises" />,
        accessor: 'premiseLimit',
        Cell: ({ row }) => (
          <TableCell>{getFeatureValue(row.original.features, 'premise', 'limit')}</TableCell>
        ),
        width: '12%',
      },

      {
        Header: <IntlMessages id="nav.geofences" />,
        accessor: 'geofenceLimit',
        Cell: ({ row }) => (
          <TableCell>{getFeatureValue(row.original.features, 'geofence', 'limit')}</TableCell>
        ),
        width: '12%',
      },

      {
        Header: <IntlMessages id="features.premise.range" />,
        accessor: 'premiseRange',
        Cell: ({ row }) => (
          <TableCell>{getFeatureValue(row.original.features, 'premise', 'range')}</TableCell>
        ),
        width: '12%',
      },

      {
        Header: <IntlMessages id="nav.cvd" />,
        accessor: 'edecLimit',
        Cell: ({ row }) => (
          <TableCell>{getFeatureValue(row.original.features, 'edec', 'limit')}</TableCell>
        ),
        width: '12%',
      },

      {
        Header: <IntlMessages id="table.col.actions" />,
        accessor: 'actions',
        isAction: true,
        width: '15%',
      },
    ],
    []
  );

  const addOnColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.role" />,
        accessor: 'name',
        width: '20%',
      },
      {
        Header: <IntlMessages id="table.col.price" />,
        accessor: 'baseCost',
        Cell: (cellData) => <TableCell>$ {cellData.value}</TableCell>,
        width: '20%',
      },
      {
        Header: 'Type',
        accessor: 'addOn',
        Cell: (cellData) => (
          <TableCell style={{ textTransform: 'capitalize' }}>
            {cellData.value ? cellData.value : '--'}
          </TableCell>
        ),
        width: '20%',
      },
      {
        Header: 'Quantity',
        accessor: 'qty',
        Cell: (cellData) => <TableCell>{cellData.value ? cellData.value : '--'}</TableCell>,
        width: '20%',
      },

      {
        Header: <IntlMessages id="table.col.actions" />,
        accessor: 'actions',
        isAction: true,
        width: '20%',
      },
    ],
    []
  );

  const actionList = [
    (data) => {
      const isEditDisabled = data?.baseCost === null || data?.baseCost === undefined;

      return (
        <IconButton
          onClick={() => !isEditDisabled && setEditSubscriptionPlan(data)}
          disabled={isEditDisabled}
          style={isEditDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          <EditIcon style={{ width: '18px', height: '18px' }} />
        </IconButton>
      );
    },
    (data) => {
      const isDeleteDisabled = data?.baseCost === null || data?.baseCost === undefined;

      return (
        <IconButton
          onClick={() => !isDeleteDisabled && setDeletePlan(data)}
          disabled={isDeleteDisabled}
          style={isDeleteDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          <DeleteIcon style={{ width: '18px', height: '18px' }} />
        </IconButton>
      );
    },
  ];

  const addOnActionList = [
    (data) => (
      <IconButton onClick={() => setDeletePlan(data)}>
        <DeleteIcon style={{ width: '18px', height: '18px' }} />
      </IconButton>
    ),
  ];

  const handleClosedDeletePlan = () => {
    setDeletePlan(false);
  };
  const handleClosedSubscription = () => {
    setEditSubscriptionPlan(false);
  };
  const handleClosedCreatePlan = () => {
    setCreatePlan(false);
  };
  const handleClosedAddOnPlan = () => {
    setAddOnPlan(false);
  };
  const handleDeletePlan = (data) => {
    deleteMutation.mutate(data.id);
  };

  if (query.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  const excelFormatData = extractData(query.data);
  const fileName = `Subscription-Plan-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const columnConfig = [
    { header: 'Subscription Name', key: 'planName', width: 30 },
    { header: 'Subscription Price', key: 'planPrice', width: 30 },
  ];

  const handleClickItem = (item) => {
    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        break;
      default:
        break;
    }
  };

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.subscriptionPlans" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '10px' }}>
          {excelFormatData && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
          <IconButtonWithText
            text={<IntlMessages id="button.text.create.new.plan" />}
            icon={<AddPlusIcon />}
            onClick={() => {
              setCreatePlan(true);
            }}
          />
          {/* <IconButtonWithText
            text={<IntlMessages id="button.text.add.on" />}
            icon={<AddPlusIcon />}
            onClick={() => {
              setAddOnPlan(true);
            }}
          /> */}
        </TwoColumns>
      </CompanyContainer>
      <TableWrapper>
        <Table
          data={tableData}
          columns={userColumns}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={query?.data?.length || 0}
          actions={actionList}
        />
      </TableWrapper>
      {/* <HeaderTitle>Add On Plans</HeaderTitle> */}
      {/* <TableWrapper>
        <Table
          data={tableDataAddOn}
          columns={addOnColumns}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={tableDataAddOn?.length || 0}
          actions={addOnActionList}
        />
      </TableWrapper>{' '} */}
      {deletePlan && (
        <DeleteConfirmationModal
          onClose={handleClosedDeletePlan}
          message={
            <IntlMessages
              id="delete.plan.confirmation.info"
              values={{ planName: deletePlan.name }}
            />
          }
          onConfirm={() => handleDeletePlan(deletePlan)}
        />
      )}
      {editSubscriptionPlan && (
        <SubscriptionModalV2
          onClose={handleClosedSubscription}
          data={editSubscriptionPlan}
          actionEditOrCreatePlan="editPlan"
        />
      )}
      {createPlan && (
        <SubscriptionModalV2
          onClose={handleClosedCreatePlan}
          data={CreateSubscriptionPlanDefaultValues}
          actionEditOrCreatePlan="createPlan"
        />
      )}
      {addOnPlan && <AddOnPlan onClose={handleClosedAddOnPlan} data={addOnDefaultValues} />}
    </StyledContainer>
  );
};

export default SubscriptionPlan;
